import { useState } from "react";
import {
  useCreate,
  useGetIdentity,
  useGetList,
  useNotify,
  useRefresh,
  useUpdate,
} from "ra-core";
import {
  Check,
  ChevronsUpDown,
  ListPlus,
  UserPlus,
  ArrowLeft,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { getContactDisplayName } from "../contacts/contactModel";
import type { Contact, NextActionType } from "../types";

// Sprint 10.x — Quick Add Action (spec Théo 2026-05-13 + 2026-05-15) :
// "ajouter une action, commentaire, date, échéance max, lié contact/entreprise".
//
// Pose directement next_action_type + next_action_date sur un contact pour
// que l'action remonte dans MyDayActionsList. Commentaire archivé comme
// contact_note dans la timeline.
//
// Sprint 10.x 2026-05-15 : recherche par email (côté dataProvider FTS étendu)
// + création inline d'un contact à la volée quand la personne n'est pas
// encore dans le CRM. Contact créé avec population='prospection' par défaut
// (un sales_id se pose via le trigger set_contact_sales_id_trigger).

const TYPE_OPTIONS: { value: NextActionType; label: string }[] = [
  { value: "a_relancer", label: "Relancer" },
  { value: "a_appeler", label: "Appeler" },
  { value: "a_mailer", label: "Envoyer démo" },
  { value: "a_engager", label: "Engager" },
];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function looksLikeEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

type NewContactDraft = {
  first_name: string;
  last_name: string;
  email: string;
  linkedin_url: string;
};

function seedDraftFromSearch(search: string): NewContactDraft {
  const trimmed = search.trim();
  if (!trimmed) {
    return { first_name: "", last_name: "", email: "", linkedin_url: "" };
  }
  if (looksLikeEmail(trimmed)) {
    return {
      first_name: "",
      last_name: "",
      email: trimmed.toLowerCase(),
      linkedin_url: "",
    };
  }
  // Sinon split sur le 1er espace : 1er mot = prénom, reste = nom
  const parts = trimmed.split(/\s+/);
  const [first, ...rest] = parts;
  return {
    first_name: first ?? "",
    last_name: rest.join(" "),
    email: "",
    linkedin_url: "",
  };
}

export function QuickAddActionDialog({
  trigger,
}: {
  trigger?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [contactSearchOpen, setContactSearchOpen] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [pickerMode, setPickerMode] = useState<"search" | "create">("search");
  const [draft, setDraft] = useState<NewContactDraft>({
    first_name: "",
    last_name: "",
    email: "",
    linkedin_url: "",
  });
  const [type, setType] = useState<NextActionType>("a_relancer");
  const [echeance, setEcheance] = useState(todayIso());
  const [comment, setComment] = useState("");

  const { identity } = useGetIdentity();
  const [createNote, { isPending: creatingNote }] = useCreate();
  const [createContact, { isPending: creatingContact }] = useCreate();
  const [updateContact, { isPending: updatingContact }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const isSubmitting = creatingNote || updatingContact;

  const { data: contactResults } = useGetList<Contact>(
    "contacts_summary",
    {
      pagination: { page: 1, perPage: 20 },
      sort: { field: "last_seen", order: "DESC" },
      filter: contactSearch ? { q: contactSearch } : {},
    },
    { enabled: contactSearchOpen && pickerMode === "search" },
  );

  const reset = () => {
    setSelectedContact(null);
    setContactSearch("");
    setPickerMode("search");
    setDraft({ first_name: "", last_name: "", email: "", linkedin_url: "" });
    setType("a_relancer");
    setEcheance(todayIso());
    setComment("");
  };

  const enterCreateMode = () => {
    setDraft(seedDraftFromSearch(contactSearch));
    setPickerMode("create");
  };

  const cancelCreateMode = () => {
    setPickerMode("search");
  };

  const submitCreateContact = async () => {
    const firstName = draft.first_name.trim();
    const lastName = draft.last_name.trim();
    const email = draft.email.trim().toLowerCase();
    const linkedin = draft.linkedin_url.trim();

    if (!firstName && !lastName && !email) {
      notify("Renseigne au moins un nom ou un email", { type: "warning" });
      return;
    }
    if (email && !looksLikeEmail(email)) {
      notify("Email invalide", { type: "warning" });
      return;
    }

    try {
      const newContact = await new Promise<Contact>((resolve, reject) =>
        createContact(
          "contacts",
          {
            data: {
              first_name: firstName || null,
              last_name: lastName || null,
              email_jsonb: email ? [{ email, type: "Work" }] : null,
              linkedin_url: linkedin || null,
              // population default 'prospection' via DB default
              // sales_id auto-posé via trigger set_contact_sales_id_trigger
            },
          },
          {
            onSuccess: (data) => resolve(data as Contact),
            onError: (err) => reject(err),
          },
        ),
      );
      setSelectedContact(newContact);
      setContactSearchOpen(false);
      setPickerMode("search");
      setContactSearch("");
      notify("Contact créé", { type: "success" });
    } catch {
      notify("Impossible de créer le contact", { type: "error" });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedContact) {
      notify("Sélectionne un contact lié à l'action", { type: "warning" });
      return;
    }

    // 1. Note commentaire (si fourni) — dans la timeline.
    const trimmed = comment.trim();
    if (trimmed) {
      try {
        await new Promise<void>((resolve, reject) =>
          createNote(
            "contact_notes",
            {
              data: {
                contact_id: selectedContact.id,
                text: trimmed,
                sales_id: identity?.id,
                date: new Date().toISOString(),
              },
            },
            { onSuccess: () => resolve(), onError: (err) => reject(err) },
          ),
        );
      } catch {
        notify("Impossible d'ajouter le commentaire", { type: "error" });
        return;
      }
    }

    // 2. Pose next_action_type + next_action_date sur le contact.
    try {
      await new Promise<void>((resolve, reject) =>
        updateContact(
          "contacts",
          {
            id: selectedContact.id,
            data: { next_action_type: type, next_action_date: echeance },
            previousData: selectedContact,
          },
          {
            mutationMode: "optimistic",
            onSuccess: () => resolve(),
            onError: (err) => reject(err),
          },
        ),
      );
      notify(
        `Action posée pour ${selectedContact.first_name ?? "le contact"}`,
        {
          type: "success",
        },
      );
      refresh();
      reset();
      setOpen(false);
    } catch {
      notify("Impossible de poser l'action", { type: "error" });
    }
  };

  // Affichage du contact sélectionné : utilise getContactDisplayName qui
  // bascule sur l'email quand le nom est "Pending Pending" (placeholder
  // appliqué par fabrik-1 avant que l'user complète son profil).
  const fullName = selectedContact
    ? getContactDisplayName(selectedContact)
    : "";
  const subtitle = selectedContact?.company_name
    ? ` — ${selectedContact.company_name}`
    : "";

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => (o ? setOpen(o) : (reset(), setOpen(o)))}
    >
      {trigger ? (
        <div onClick={() => setOpen(true)}>{trigger}</div>
      ) : (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setOpen(true)}
          className="gap-1.5"
        >
          <ListPlus className="size-3.5" />
          Ajouter une action
        </Button>
      )}
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nouvelle action</DialogTitle>
          <DialogDescription>
            Pose une action datée sur un contact. Elle apparaîtra dans "Action
            du jour" dès que la date d'échéance arrive.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-2">
          <div>
            <Label className="text-[12px]">Contact ou entreprise</Label>
            <Popover
              open={contactSearchOpen}
              onOpenChange={(o) => {
                setContactSearchOpen(o);
                if (!o) setPickerMode("search");
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={contactSearchOpen}
                  className="w-full justify-between mt-1 font-normal"
                >
                  {selectedContact
                    ? `${fullName}${subtitle}`
                    : "Chercher par nom, entreprise ou email…"}
                  <ChevronsUpDown className="size-3.5 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-[--radix-popover-trigger-width] p-0"
                align="start"
              >
                {pickerMode === "search" ? (
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Marie, Acme, contact@…"
                      value={contactSearch}
                      onValueChange={setContactSearch}
                    />
                    <CommandList>
                      <CommandEmpty>
                        <div className="px-2 py-3 text-[12.5px] text-fabrik-text-3">
                          Aucun résultat
                        </div>
                      </CommandEmpty>
                      {contactResults && contactResults.length > 0 && (
                        <CommandGroup heading="Contacts existants">
                          {contactResults.map((c) => (
                            <CommandItem
                              key={c.id}
                              value={String(c.id)}
                              onSelect={() => {
                                setSelectedContact(c);
                                setContactSearchOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "size-3.5 mr-2",
                                  selectedContact?.id === c.id
                                    ? "opacity-100"
                                    : "opacity-0",
                                )}
                              />
                              <div className="flex flex-col min-w-0">
                                <span className="text-[13px] truncate">
                                  {getContactDisplayName(c)}
                                </span>
                                <ContactSubLine contact={c} />
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                      {contactSearch.trim().length > 0 && (
                        <>
                          {contactResults && contactResults.length > 0 && (
                            <CommandSeparator />
                          )}
                          <CommandGroup>
                            <CommandItem
                              value="__create_new__"
                              onSelect={enterCreateMode}
                              className="text-fabrik-accent"
                            >
                              <UserPlus className="size-3.5 mr-2" />
                              <span className="text-[13px]">
                                Créer le contact «&nbsp;
                                {contactSearch.length > 40
                                  ? contactSearch.slice(0, 38) + "…"
                                  : contactSearch}
                                &nbsp;»
                              </span>
                            </CommandItem>
                          </CommandGroup>
                        </>
                      )}
                    </CommandList>
                  </Command>
                ) : (
                  <InlineCreateContact
                    draft={draft}
                    onChange={setDraft}
                    onCancel={cancelCreateMode}
                    onSubmit={submitCreateContact}
                    submitting={creatingContact}
                  />
                )}
              </PopoverContent>
            </Popover>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="qaa-type" className="text-[12px]">
                Type d'action
              </Label>
              <select
                id="qaa-type"
                value={type}
                onChange={(e) => setType(e.target.value as NextActionType)}
                className="mt-1 w-full h-9 rounded-md border border-border bg-background px-2 text-sm"
              >
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="qaa-echeance" className="text-[12px]">
                Échéance
              </Label>
              <Input
                id="qaa-echeance"
                type="date"
                value={echeance}
                onChange={(e) => setEcheance(e.target.value)}
                className="mt-1"
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="qaa-comment" className="text-[12px]">
              Détails de l'action{" "}
              <span className="text-fabrik-text-3">(optionnel)</span>
            </Label>
            <Textarea
              id="qaa-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Ex : « engager via LinkedIn », « préparer démo personnalisée », contexte, dernière interaction…"
              rows={4}
              className="mt-1"
            />
            <p className="text-[11px] text-fabrik-text-3 mt-1">
              Le type d'action ci-dessus reste contraint pour les KPIs. Mets ici
              ce qu'il faut faire concrètement — c'est archivé dans la timeline
              du contact.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
                setOpen(false);
              }}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={isSubmitting || !selectedContact}>
              {isSubmitting ? "Création…" : "Créer l'action"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function InlineCreateContact({
  draft,
  onChange,
  onCancel,
  onSubmit,
  submitting,
}: {
  draft: NewContactDraft;
  onChange: (d: NewContactDraft) => void;
  onCancel: () => void;
  onSubmit: () => void;
  submitting: boolean;
}) {
  const update = (k: keyof NewContactDraft, v: string) =>
    onChange({ ...draft, [k]: v });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <div className="p-3 flex flex-col gap-2">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[12px] font-medium text-foreground">
          Nouveau contact
        </p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 px-1.5 text-[11px] text-fabrik-text-3"
          onClick={onCancel}
        >
          <ArrowLeft className="size-3 mr-1" />
          Retour
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input
          placeholder="Prénom"
          value={draft.first_name}
          onChange={(e) => update("first_name", e.target.value)}
          onKeyDown={handleKeyDown}
          className="h-8 text-[13px]"
          autoFocus
        />
        <Input
          placeholder="Nom"
          value={draft.last_name}
          onChange={(e) => update("last_name", e.target.value)}
          onKeyDown={handleKeyDown}
          className="h-8 text-[13px]"
        />
      </div>
      <Input
        type="email"
        placeholder="Email"
        value={draft.email}
        onChange={(e) => update("email", e.target.value)}
        onKeyDown={handleKeyDown}
        className="h-8 text-[13px]"
      />
      <Input
        type="url"
        placeholder="https://linkedin.com/in/…"
        value={draft.linkedin_url}
        onChange={(e) => update("linkedin_url", e.target.value)}
        onKeyDown={handleKeyDown}
        className="h-8 text-[13px]"
      />
      <div className="flex justify-end gap-2 mt-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={submitting}
        >
          Annuler
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={onSubmit}
          disabled={
            submitting ||
            (!draft.first_name.trim() &&
              !draft.last_name.trim() &&
              !draft.email.trim())
          }
        >
          {submitting ? "Création…" : "Créer & sélectionner"}
        </Button>
      </div>
      <p className="text-[10.5px] text-fabrik-text-3 mt-0.5">
        Créé avec population «&nbsp;prospection&nbsp;». Tu pourras compléter la
        fiche plus tard.
      </p>
    </div>
  );
}

// Sous-ligne d'un résultat de recherche : affiche l'email du contact (si
// existant) et son entreprise (si existante). Évite d'afficher l'email
// quand celui-ci est déjà utilisé comme libellé principal (cas Pending
// Pending où getContactDisplayName a basculé sur l'email).
function ContactSubLine({ contact }: { contact: Contact }) {
  const displayName = getContactDisplayName(contact);
  const primaryEmail = (
    contact.email_jsonb as Array<{ email: string }> | undefined
  )?.[0]?.email;
  const showEmail = !!primaryEmail && primaryEmail !== displayName;
  const company = contact.company_name;

  if (!showEmail && !company) return null;

  return (
    <span className="text-[11px] text-fabrik-text-3 truncate">
      {showEmail && <>{primaryEmail}</>}
      {showEmail && company && " · "}
      {company && <>{company}</>}
    </span>
  );
}
